import React, { useState, useEffect, useCallback } from "react";
import type { GameName, LeaderboardEntry, DailyFiveEntry } from "../types.ts";
import { ALL_GAMES } from "../types.ts";

const GAME_META: Record<GameName, { emoji: string; color: string }> = {
  foldaway: { emoji: "\uD83D\uDCD0", color: "#A78BFA" },
  spillover: { emoji: "\uD83D\uDCA7", color: "#4ECAFF" },
  lockstep: { emoji: "\u2699\uFE0F", color: "#FFD166" },
  burnout: { emoji: "\uD83D\uDD25", color: "#FF6B6B" },
  echo: { emoji: "\uD83D\uDCE1", color: "#00F0A0" },
};

export function Leaderboard() {
  const [tab, setTab] = useState<GameName | "daily-five">("foldaway");
  const [gameEntries, setGameEntries] = useState<LeaderboardEntry[]>([]);
  const [d5Entries, setD5Entries] = useState<DailyFiveEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "daily-five") {
        const res = await fetch("/api/leaderboard/daily-five");
        if (res.ok) setD5Entries(await res.json());
      } else {
        const res = await fetch(`/api/leaderboard/${tab}`);
        if (res.ok) setGameEntries(await res.json());
      }
    } catch {}
    setLoading(false);
  }, [tab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="pt-[80px] pb-16">
      <div className="font-mono text-[10px] tracking-[5px] uppercase text-electric-mint-dim mb-2">
        Rankings
      </div>
      <h2 className="font-display font-extrabold text-3xl tracking-[-1px] mb-6">Leaderboard</h2>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap mb-8">
        {ALL_GAMES.map((g) => {
          const m = GAME_META[g];
          return (
            <button
              key={g}
              onClick={() => setTab(g)}
              className={`font-display font-semibold text-xs px-4 py-2 rounded-full border transition-all duration-200 ${
                tab === g
                  ? "border-opacity-60"
                  : "border-opacity-20 opacity-60 hover:opacity-100"
              }`}
              style={{
                color: m.color,
                borderColor: m.color,
                background: tab === g ? `${m.color}15` : "transparent",
              }}
            >
              {m.emoji} {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          );
        })}
        <button
          onClick={() => setTab("daily-five")}
          className={`font-display font-semibold text-xs px-4 py-2 rounded-full border transition-all duration-200 ${
            tab === "daily-five"
              ? "border-electric-mint text-electric-mint bg-[rgba(0,240,160,0.08)]"
              : "border-[rgba(255,255,255,0.15)] text-chalk-dim opacity-60 hover:opacity-100"
          }`}
        >
          Daily Five
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-warm-gray text-sm">Loading...</p>
      ) : tab === "daily-five" ? (
        d5Entries.length === 0 ? (
          <p className="text-warm-gray text-sm">No Daily Five completions today yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[9px] tracking-[2px] uppercase text-warm-gray border-b border-dark-gray">
                <th className="pb-3 pr-4">#</th>
                <th className="pb-3 pr-4">Player</th>
                <th className="pb-3 pr-4">Stars</th>
                <th className="pb-3">Games</th>
              </tr>
            </thead>
            <tbody>
              {d5Entries.map((e) => (
                <tr key={e.rank} className="border-b border-[rgba(255,255,255,0.04)]">
                  <td className="py-3 pr-4 font-mono text-mid-gray">{e.rank}</td>
                  <td className="py-3 pr-4 font-display font-medium flex items-center gap-2">
                    {e.avatar_url ? (
                      <img src={e.avatar_url} alt="" className="w-5 h-5 rounded-full" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-surface" />
                    )}
                    {e.display_name}
                  </td>
                  <td className="py-3 pr-4 text-amber-spark font-mono">{e.total_stars}/15</td>
                  <td className="py-3 font-mono">{e.games_done}/5</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : gameEntries.length === 0 ? (
        <p className="text-warm-gray text-sm">No completions today yet. Be the first!</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[9px] tracking-[2px] uppercase text-warm-gray border-b border-dark-gray">
              <th className="pb-3 pr-4">#</th>
              <th className="pb-3 pr-4">Player</th>
              <th className="pb-3 pr-4">Stars</th>
              <th className="pb-3 pr-4">Metric</th>
              <th className="pb-3">Time</th>
            </tr>
          </thead>
          <tbody>
            {gameEntries.map((e) => (
              <tr key={e.rank} className="border-b border-[rgba(255,255,255,0.04)]">
                <td className="py-3 pr-4 font-mono text-mid-gray">{e.rank}</td>
                <td className="py-3 pr-4 font-display font-medium flex items-center gap-2">
                  {e.avatar_url ? (
                    <img src={e.avatar_url} alt="" className="w-5 h-5 rounded-full" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-surface" />
                  )}
                  {e.display_name}
                </td>
                <td className="py-3 pr-4 text-amber-spark">
                  {"\u2605".repeat(e.stars)}{"\u2606".repeat(3 - e.stars)}
                </td>
                <td className="py-3 pr-4 font-mono">{e.metric}</td>
                <td className="py-3 font-mono text-warm-gray">
                  {Math.floor(e.time_ms / 60000)}:{String(Math.floor((e.time_ms % 60000) / 1000)).padStart(2, "0")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
